import { useCallback, useEffect, useState } from 'react';
import { localStore } from '../utils/storageWrapper';

// Based on useSessionStorage.ts

// A wrapper for "JSON.parse()"" to support "undefined" value
function parseJSON<T>(value: string | null): T | undefined {
  try {
    return value === 'undefined' ? undefined : JSON.parse(value ?? '');
  } catch {
    return undefined;
  }
}

type UseLocalStorageReturn<T> = [
  value: T,
  setValue: (value: T) => void,
  remove: () => void,
];

type Options<T> = {
  /**
   * string result from localStorage. Will require JSON parsing
   */
  parse?: (value: string) => T;
  /**
   * write to storage. Ensure to JSON stringify any objects.
   * This only applies to localStorage, not the state
   */
  save?: (value: T) => string;
};

const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  options: Options<T> = {},
): UseLocalStorageReturn<T> => {
  const readValue = useCallback((): T => {
    if (!key || typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = localStore.getItem(key) ?? '';
      const value = options.parse
        ? options.parse(item)
        : parseJSON(item);

      return item ? (value as T) : initialValue;
    } catch (error) {
      return initialValue;
    }
  }, [initialValue, key, options]);

  const serialize = useCallback((value: T) => (
    options.save
      ? options.save(value)
      : JSON.stringify(value)
  ), [options]);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(initialValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback((value) => {
    // Prevent build error "window is undefined" but keeps working
    if (!key || typeof window === 'undefined') return;

    // Allow value to be a function so we have the same API as useState
    const newValue = value instanceof Function ? value(storedValue) : value;
    const serialized = serialize(newValue);

    // Avoid render loops
    if (serialize(storedValue) === serialized) return;

    // Save to session storage
    localStore.setItem(key, serialized);

    // Save state
    setStoredValue(newValue);
  }, [serialize, key, storedValue]);

  useEffect(() => {
    if (!key) return;
    setStoredValue(readValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = useCallback(() => {
    localStore.removeItem(key);
    setStoredValue(undefined);
  }, [key]);

  return [storedValue, setValue, clear];
};

export default useLocalStorage;
